import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Building2, 
  MapPin, 
  Check, 
  Star,
  Filter,
  ChevronDown,
  ExternalLink,
  DollarSign,
  Users,
  Bed,
  LogOut,
  Loader2,
  AlertCircle,
  List,
  Grid3X3,
  ImageOff,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  X,
  Globe,
  Eye,
  Plane,
  StickyNote,
  Clock,
  FileCheck,
  XCircle,
  Layers,
  Calendar,
  Utensils
} from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { Modal } from '@/components/ui/Modal/Modal';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { proposalService } from '@/services/ProposalService';
import { useVenuePhotos } from '@/hooks/useVenuePhotos';
import { useAuthStore } from '@/stores/authStore';
import { StarRating } from '@/components/ui/StarRating';
import { cn } from '@/lib/utils';
import { formatEventDates, type EventDateFields } from '@/lib/dateFormatters';
import { formatCurrency } from '@/lib/currencyFormat';

// ============================================
// Theme Context
// ============================================
type ThemeMode = 'light' | 'dark';

const ThemeContext = createContext<{
  theme: ThemeMode;
  toggleTheme: () => void;
}>({
  theme: 'dark',
  toggleTheme: () => {},
});

function useTheme() {
  return useContext(ThemeContext);
}

// ============================================
// Storage & Session
// ============================================
const THEME_STORAGE_KEY = 'share_theme_preference';

import { 
  getStoredSession, 
  clearSession,
  type StoredSession 
} from '@/services/PublicSessionManager';

function getStoredTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {}
  return 'dark';
}

function setStoredTheme(theme: ThemeMode) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {}
}

// ============================================
// Data Interfaces
// ============================================
interface VenueData {
  id: string;
  property_name: string;
  city: string;
  country: string;
  star_rating?: number;
  number_of_rooms?: number;
  maximum_capacity?: number;
  website?: string;
  address?: string;
  place_id?: string;
  tripadvisor_rating?: number;
  nearest_airport_iata?: string;
  nearest_airport_distance_km?: number;
  approval_status?: 'approved' | 'denied' | null;
  status?: string;
  match_score?: number;
  cvent_lowest_room_rate?: number;
  cvent_total_cost?: number;
  cvent_currency_code?: string;
  cvent_proposal_status?: string;
  is_all_inclusive?: boolean;
  is_manual_proposal?: boolean;
  // Only client-facing hotel notes are ever returned by the public API.
  notes?: string | null;
  cvent_food_beverage_minimum?: number | null;
  available_dates?: string | null;
}

interface DestinationGroup {
  city: string;
  country: string;
  destination_type?: 'city' | 'region' | 'country';
  display_name?: string | null;
  venues: VenueData[];
}

/** A client-facing hotel note, shown beside the hotel. */
function ClientNote({ notes, isDark, compact = false }: { notes?: string | null; isDark: boolean; compact?: boolean }) {
  if (!notes?.trim()) return null;
  return (
    <div
      className={cn(
        'rounded border-l-2 border-secondary-500',
        compact ? 'p-2 text-sm' : 'p-4 text-sm',
        isDark ? 'bg-white/5' : 'bg-secondary-50'
      )}
      data-testid="client-note"
    >
      <span className={cn('font-medium block mb-1', compact ? 'text-xs' : 'text-sm', isDark ? 'text-gray-300' : 'text-gray-700')}>
        Moniker's notes
      </span>
      <p className={cn('whitespace-pre-wrap', isDark ? 'text-gray-300' : 'text-gray-600')}>{notes}</p>
    </div>
  );
}

function AllInclusiveBadge({ isDark, compact = false }: { isDark: boolean; compact?: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full bg-white font-medium shadow-sm',
        compact ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
        isDark ? 'border border-white/20 text-gray-900' : 'border border-gray-200 text-gray-900'
      )}
    >
      <Utensils className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      All-Inclusive
    </span>
  );
}

interface VenueFilters {
  min_rooms?: number | null;
  min_conference_capacity?: number | null;
  star_rating?: number | null;
  max_airport_distance_km?: number | null;
  has_spa?: boolean | null;
  min_restaurants?: number | null;
  min_bars?: number | null;
  min_pools?: number | null;
  beach_access?: 'beachfront' | 'beach_access' | 'none' | null;
  all_inclusive?: boolean | null;
  property_type?: 'adults_only' | 'family_friendly' | 'mixed' | null;
  has_golf?: boolean | null;
  min_tripadvisor_rating?: number | null;
}

interface ResearchData {
  event: {
    name: string;
    client_name: string;
    venue_filters?: VenueFilters | null;
    // Date fields
    date_type?: 'date_range' | 'month' | null;
    event_month?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    date_display_override?: string | null;
    // Budget and group size
    budget_min?: number | null;
    budget_max?: number | null;
    expected_attendees?: number | null;
  };
  approved: DestinationGroup[];
  shortlisted: DestinationGroup[];
  all_venues: DestinationGroup[];
}

type TabType = 'approved' | 'shortlisted' | 'all';

// ============================================
// Theme Toggle Component
// ============================================
function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  
  return (
    <button
      onClick={toggleTheme}
      className={cn(
        "relative w-16 h-8 rounded-full transition-all duration-300 flex items-center",
        isDark 
          ? "bg-gray-700 border border-gray-600" 
          : "bg-gray-200 border border-gray-300"
      )}
      aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
    >
      <span
        className={cn(
          "absolute w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm",
          isDark 
            ? "left-1 bg-gray-900 text-secondary-400" 
            : "left-[34px] bg-white text-secondary-600"
        )}
      >
        {isDark ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
      </span>
    </button>
  );
}

// ============================================
// Research Requirements Display Component
// ============================================
interface ResearchRequirementsProps {
  venueFilters: VenueFilters | null | undefined;
  isDark: boolean;
}

function ResearchRequirements({ venueFilters, isDark }: ResearchRequirementsProps) {
  if (!venueFilters) return null;

  const getFilterChips = (): Array<{ label: string; key: string }> => {
    const chips: Array<{ label: string; key: string }> = [];

    if (venueFilters.min_rooms) {
      chips.push({ label: `${venueFilters.min_rooms}+ rooms`, key: 'min_rooms' });
    }
    if (venueFilters.min_conference_capacity) {
      chips.push({ label: `Conference ${venueFilters.min_conference_capacity}+`, key: 'min_conference_capacity' });
    }
    if (venueFilters.max_airport_distance_km) {
      chips.push({ label: `<${venueFilters.max_airport_distance_km}km airport`, key: 'max_airport_distance_km' });
    }
    if (venueFilters.star_rating) {
      chips.push({ label: `${venueFilters.star_rating}+ stars`, key: 'star_rating' });
    }
    if (venueFilters.has_spa) {
      chips.push({ label: 'Spa', key: 'has_spa' });
    }
    if (venueFilters.min_restaurants) {
      chips.push({ 
        label: `${venueFilters.min_restaurants}+ Restaurant${venueFilters.min_restaurants > 1 ? 's' : ''}`, 
        key: 'min_restaurants' 
      });
    }
    if (venueFilters.min_bars) {
      chips.push({ 
        label: `${venueFilters.min_bars}+ Bar${venueFilters.min_bars > 1 ? 's' : ''}`, 
        key: 'min_bars' 
      });
    }
    if (venueFilters.min_pools) {
      chips.push({ 
        label: `${venueFilters.min_pools}+ Pool${venueFilters.min_pools > 1 ? 's' : ''}`, 
        key: 'min_pools' 
      });
    }
    if (venueFilters.beach_access === 'beachfront') {
      chips.push({ label: 'Beachfront', key: 'beach_access' });
    }
    if (venueFilters.beach_access === 'beach_access') {
      chips.push({ label: 'Beach Access', key: 'beach_access' });
    }
    if (venueFilters.all_inclusive) {
      chips.push({ label: 'All-Inclusive', key: 'all_inclusive' });
    }
    if (venueFilters.property_type === 'adults_only') {
      chips.push({ label: 'Adults Only', key: 'property_type' });
    }
    if (venueFilters.property_type === 'family_friendly') {
      chips.push({ label: 'Family Friendly', key: 'property_type' });
    }
    if (venueFilters.property_type === 'mixed') {
      chips.push({ label: 'Mixed', key: 'property_type' });
    }
    if (venueFilters.has_golf) {
      chips.push({ label: 'Golf', key: 'has_golf' });
    }
    if (venueFilters.min_tripadvisor_rating) {
      chips.push({ 
        label: `TripAdvisor ${venueFilters.min_tripadvisor_rating}+`, 
        key: 'min_tripadvisor_rating' 
      });
    }

    return chips;
  };

  const chips = getFilterChips();
  if (chips.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={cn("text-sm whitespace-nowrap", isDark ? "text-gray-400" : "text-gray-500")}>
        Research Requirements:
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        {chips.map((chip) => (
          <Badge
            key={chip.key}
            variant="secondary"
            className={cn(
              "text-xs",
              isDark ? "bg-gray-800 text-gray-300 border-gray-700" : "bg-gray-100 text-gray-700"
            )}
          >
            {chip.label}
          </Badge>
        ))}
      </div>
    </div>
  );
}

// ============================================
// Venue Details Modal (Simplified Public Version)
// ============================================
interface VenueDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  venue: VenueData | null;
}

function VenueDetailsModal({ isOpen, onClose, venue, shareLinkToken, sessionId }: VenueDetailsModalProps & { shareLinkToken?: string; sessionId?: string }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { photos, isLoading: isLoadingPhotos } = useVenuePhotos(venue?.id, isOpen, shareLinkToken, sessionId);
  
  if (!venue) return null;
  
  // Custom modal wrapper for dark mode - we'll render our own header/content
  if (isDark) {
    return (
      <div
        className={cn(
          "fixed inset-0 z-50 overflow-y-auto",
          isOpen ? "block" : "hidden"
        )}
        role="dialog"
        aria-modal="true"
      >
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/70 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal Container */}
        <div className="flex min-h-full items-start justify-center p-4 sm:p-6 md:p-8">
          <div
            className="relative w-full max-w-2xl my-8 bg-gray-900 rounded-xl shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-800">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-semibold text-white">
                  {venue.property_name}
                </h2>
                {venue.is_all_inclusive && <AllInclusiveBadge isDark={isDark} />}
              </div>
              <button
                onClick={onClose}
                className="ml-4 rounded-md p-1 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
                aria-label="Close modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Image Gallery */}
              <div className="relative h-64 rounded-lg overflow-hidden bg-gray-800">
                {isLoadingPhotos ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
                  </div>
                ) : photos.length > 0 ? (
                  <>
                    <img
                      src={photos[currentImageIndex]?.url}
                      alt={`${venue.property_name} - Photo ${currentImageIndex + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {photos.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : photos.length - 1)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(prev => prev < photos.length - 1 ? prev + 1 : 0)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded-full bg-black/50 text-white text-xs">
                          {currentImageIndex + 1} / {photos.length}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <ImageOff className="w-12 h-12 text-gray-600 mb-2" />
                    <span className="text-sm text-gray-500">No images available</span>
                  </div>
                )}
              </div>
              
              {/* Venue Info */}
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-400">
                      {venue.city}{venue.country ? `, ${venue.country}` : ''}
                    </p>
                    {venue.address && (
                      <p className="text-sm mt-1 text-gray-500">{venue.address}</p>
                    )}
                  </div>
                  {venue.star_rating && (
                    <StarRating rating={venue.star_rating} size="md" showEmptyStars={true} />
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {venue.number_of_rooms && (
                    <div className="p-3 rounded-lg text-center bg-gray-800">
                      <Building2 className="w-5 h-5 mx-auto mb-1 text-primary-500" />
                      <div className="font-semibold text-white">{venue.number_of_rooms}</div>
                      <div className="text-xs text-gray-400">Rooms</div>
                    </div>
                  )}
                  {venue.maximum_capacity && (
                    <div className="p-3 rounded-lg text-center bg-gray-800">
                      <Users className="w-5 h-5 mx-auto mb-1 text-primary-500" />
                      <div className="font-semibold text-white">{venue.maximum_capacity}</div>
                      <div className="text-xs text-gray-400">Max Capacity</div>
                    </div>
                  )}
                  {venue.cvent_lowest_room_rate && (
                    <div className="p-3 rounded-lg text-center bg-gray-800">
                      <DollarSign className="w-5 h-5 mx-auto mb-1 text-primary-500" />
                      <div className="font-semibold text-white">
                        {formatCurrency(venue.cvent_lowest_room_rate, venue.cvent_currency_code)}
                      </div>
                      <div className="text-xs text-gray-400">Room Rate</div>
                    </div>
                  )}
                  <div className="p-3 rounded-lg text-center bg-gray-800">
                    <Star className="w-5 h-5 mx-auto mb-1 text-primary-500" />
                    <div className="font-semibold text-white">
                      {(() => {
                        const rating = venue.tripadvisor_rating;
                        if (rating == null) return '-';
                        const numRating = typeof rating === 'number' ? rating : parseFloat(String(rating));
                        return isNaN(numRating) ? '-' : `${numRating.toFixed(1)}/5`;
                      })()}
                    </div>
                    <div className="text-xs text-gray-400">TripAdvisor</div>
                  </div>
                </div>
                
                {/* Client-facing hotel note */}
                <ClientNote notes={venue.notes} isDark />
                
                {/* Website Button */}
                {venue.website && (
                  <button
                    onClick={() => window.open(venue.website, '_blank')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-600 text-gray-200 bg-gray-800/50 hover:bg-gray-700 hover:text-white hover:border-gray-500 transition-colors"
                  >
                    Visit Website
                    <ExternalLink className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Light mode - use standard Modal component
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={venue.property_name} size="lg">
      <div className="space-y-6">
        {/* Image Gallery */}
        <div className="relative h-64 rounded-lg overflow-hidden bg-gray-100">
          {isLoadingPhotos ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : photos.length > 0 ? (
            <>
              <img
                src={photos[currentImageIndex]?.url}
                alt={`${venue.property_name} - Photo ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
              />
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev > 0 ? prev - 1 : photos.length - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev < photos.length - 1 ? prev + 1 : 0)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded-full bg-black/50 text-white text-xs">
                    {currentImageIndex + 1} / {photos.length}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <ImageOff className="w-12 h-12 text-gray-300 mb-2" />
              <span className="text-sm text-gray-400">No images available</span>
            </div>
          )}
        </div>
        
        {/* Venue Info */}
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-600">
                {venue.city}{venue.country ? `, ${venue.country}` : ''}
              </p>
              {venue.address && (
                <p className="text-sm mt-1 text-gray-500">{venue.address}</p>
              )}
            </div>
            {venue.star_rating && (
              <span className="text-lg">{'⭐'.repeat(venue.star_rating)}</span>
            )}
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {venue.number_of_rooms && (
              <div className="p-3 rounded-lg text-center bg-gray-50">
                <Building2 className="w-5 h-5 mx-auto mb-1 text-primary-500" />
                <div className="font-semibold text-gray-900">{venue.number_of_rooms}</div>
                <div className="text-xs text-gray-500">Rooms</div>
              </div>
            )}
            {venue.maximum_capacity && (
              <div className="p-3 rounded-lg text-center bg-gray-50">
                <Users className="w-5 h-5 mx-auto mb-1 text-primary-500" />
                <div className="font-semibold text-gray-900">{venue.maximum_capacity}</div>
                <div className="text-xs text-gray-500">Max Capacity</div>
              </div>
            )}
            {venue.cvent_lowest_room_rate && (
              <div className="p-3 rounded-lg text-center bg-gray-50">
                <DollarSign className="w-5 h-5 mx-auto mb-1 text-primary-500" />
                <div className="font-semibold text-gray-900">
                  {formatCurrency(venue.cvent_lowest_room_rate, venue.cvent_currency_code)}
                </div>
                <div className="text-xs text-gray-500">Room Rate</div>
              </div>
            )}
            <div className="p-3 rounded-lg text-center bg-gray-50">
              <Star className="w-5 h-5 mx-auto mb-1 text-primary-500" />
              <div className="font-semibold text-gray-900">
                {(() => {
                  const rating = venue.tripadvisor_rating;
                  if (rating == null) return '-';
                  const numRating = typeof rating === 'number' ? rating : parseFloat(String(rating));
                  return isNaN(numRating) ? '-' : `${numRating.toFixed(1)}/5`;
                })()}
              </div>
              <div className="text-xs text-gray-500">TripAdvisor</div>
            </div>
          </div>
          
          {/* Client-facing hotel note */}
          <ClientNote notes={venue.notes} isDark={false} />
          
          {/* Website Button */}
          {venue.website && (
            <Button
              variant="outline"
              onClick={() => window.open(venue.website, '_blank')}
              rightIcon={<ExternalLink className="w-4 h-4" />}
              className="w-full"
            >
              Visit Website
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ============================================
// Venue Card Component
// ============================================
interface VenueCardDisplayProps {
  venue: VenueData;
  showApprovalBorder?: boolean;
  onViewDetails?: () => void;
}

function VenueCardDisplay({ venue, showApprovalBorder = false, onViewDetails, shareLinkToken, sessionId }: VenueCardDisplayProps & { shareLinkToken?: string; sessionId?: string }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { photos, isLoading: isLoadingPhotos } = useVenuePhotos(venue.id, true, shareLinkToken, sessionId);
  
  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };
  
  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };
  
  // Determine border style based on approval status
  const getBorderStyle = () => {
    if (!showApprovalBorder) return '';
    if (venue.approval_status === 'approved') {
      return isDark 
        ? 'ring-2 ring-success-500/70 shadow-[0_0_15px_rgba(34,197,94,0.2)]'
        : 'ring-2 ring-success-500';
    }
    if (venue.approval_status === 'denied') {
      return isDark
        ? 'ring-2 ring-error-500/50 shadow-[0_0_20px_rgba(239,68,68,0.15)]'
        : 'ring-2 ring-error-500/70';
    }
    return '';
  };

  return (
    <div className={cn(
      "flex flex-col md:flex-row rounded-xl overflow-hidden transition-all duration-300",
      isDark 
        ? "bg-gray-800/50 border border-gray-700/50 hover:border-secondary-500/30" 
        : "bg-white border border-gray-200 hover:shadow-lg",
      getBorderStyle()
    )}>
      {/* Image Section */}
      <div className={cn(
        "relative w-full md:w-56 h-44 md:h-auto md:aspect-square flex-shrink-0",
        isDark ? "bg-gray-900" : "bg-gray-100"
      )}>
        {isLoadingPhotos ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className={cn("w-8 h-8 animate-spin", isDark ? "text-gray-600" : "text-gray-400")} />
          </div>
        ) : photos.length > 0 ? (
          <>
            <img
              src={photos[currentImageIndex]?.url}
              alt={`${venue.property_name} - Photo ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {photos.length > 1 && (
              <>
                <button
                  onClick={handlePrevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={handleNextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-1 rounded-full bg-black/60 text-white text-xs">
                  {currentImageIndex + 1} / {photos.length}
                </div>
              </>
            )}
          </>
        ) : (
          <div className={cn(
            "absolute inset-0 flex flex-col items-center justify-center",
            isDark ? "bg-gray-900" : "bg-gray-100"
          )}>
            <ImageOff className={cn("w-10 h-10 mb-2", isDark ? "text-gray-700" : "text-gray-300")} />
            <span className={cn("text-xs", isDark ? "text-gray-600" : "text-gray-400")}>No images</span>
          </div>
        )}
        
        {/* Status badge */}
        {showApprovalBorder && venue.approval_status && (
          <div className="absolute top-2 right-2">
            <Badge variant={venue.approval_status === 'approved' ? 'success' : 'error'} className="text-xs">
              {venue.approval_status === 'approved' ? (
                <><Check className="w-3 h-3 mr-1" />Shortlisted</>
              ) : (
                <><X className="w-3 h-3 mr-1" />Excluded</>
              )}
            </Badge>
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <div className="flex-1 p-4 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className={cn(
                "font-semibold text-base leading-tight",
                isDark ? "text-white" : "text-gray-950"
              )}>
                {venue.property_name}
              </h3>
              {venue.is_all_inclusive && <AllInclusiveBadge isDark={isDark} />}
            </div>
            <p className={cn("text-sm mt-0.5", isDark ? "text-gray-400" : "text-gray-600")}>
              {venue.city}{venue.country ? `, ${venue.country}` : ''}
            </p>
          </div>
          {venue.star_rating && (
            <div className="flex-shrink-0">
              <StarRating rating={venue.star_rating} size="sm" showEmptyStars={true} />
            </div>
          )}
        </div>
        
        {/* Stats */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3 text-sm">
          {venue.number_of_rooms && (
            <div className="flex items-center gap-2">
              <Building2 className={cn("w-4 h-4", isDark ? "text-gray-500" : "text-gray-400")} />
              <span className={isDark ? "text-gray-300" : "text-gray-700"}>{venue.number_of_rooms} rooms</span>
            </div>
          )}
          {venue.maximum_capacity && (
            <div className="flex items-center gap-2">
              <Users className={cn("w-4 h-4", isDark ? "text-gray-500" : "text-gray-400")} />
              <span className={isDark ? "text-gray-300" : "text-gray-700"}>{venue.maximum_capacity} max</span>
            </div>
          )}
          {venue.nearest_airport_iata && venue.nearest_airport_distance_km && (() => {
            const distance = typeof venue.nearest_airport_distance_km === 'number' 
              ? venue.nearest_airport_distance_km 
              : parseFloat(String(venue.nearest_airport_distance_km));
            if (isNaN(distance)) return null;
            return (
              <div className="flex items-center gap-2">
                <Plane className={cn("w-4 h-4", isDark ? "text-gray-500" : "text-gray-400")} />
                <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                  {venue.nearest_airport_iata} ({distance.toFixed(1)}km)
                </span>
              </div>
            );
          })()}
        </div>
        
        {/* Cost Information */}
        {(venue.cvent_lowest_room_rate || venue.cvent_food_beverage_minimum) && (
          <div className="flex flex-wrap gap-4 mb-3 text-sm">
            {venue.cvent_lowest_room_rate && (
              <div>
                <span className={cn("block text-xs", isDark ? "text-gray-500" : "text-gray-500")}>Room Rate</span>
                <span className="font-semibold text-primary-500">
                  {formatCurrency(venue.cvent_lowest_room_rate, venue.cvent_currency_code)}
                </span>
              </div>
            )}
            {venue.cvent_food_beverage_minimum != null && (
              <div>
                <span className={cn("block text-xs", isDark ? "text-gray-500" : "text-gray-500")}>F&B Min</span>
                <span className={cn("font-semibold", isDark ? "text-gray-300" : "text-gray-700")}>
                  {formatCurrency(venue.cvent_food_beverage_minimum, venue.cvent_currency_code)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Available Dates */}
        {venue.available_dates && (
          <div className="flex items-center gap-2 mb-3 text-sm">
            <Calendar className={cn("w-4 h-4 flex-shrink-0", isDark ? "text-gray-500" : "text-gray-400")} />
            <span className={cn("text-xs", isDark ? "text-gray-500" : "text-gray-500")}>Available:</span>
            <span className={cn("font-medium", isDark ? "text-gray-300" : "text-gray-700")}>
              {venue.available_dates}
            </span>
          </div>
        )}

        {/* RFP Proposal Status */}
        {(venue.cvent_proposal_status || venue.is_manual_proposal) && (
          <div className="mb-3">
            {(() => {
              const status = venue.cvent_proposal_status?.toLowerCase().replace(/[_\s]+/g, '_') || '';
              if (['turned_down', 'declined', 'rejected'].includes(status)) {
                return (
                  <div className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                    isDark ? "bg-error-900/30 text-error-400 border border-error-700/50" : "bg-error-50 text-error-700 border border-error-200"
                  )}>
                    <XCircle className="w-3.5 h-3.5" />
                    Declined
                  </div>
                );
              }
              // Show "Proposal Received" for both Cvent proposals and manually marked received proposals
              if (['submitted_proposal', 'submitted', 'received'].includes(status) || venue.is_manual_proposal) {
                return (
                  <div className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                    isDark ? "bg-success-900/30 text-success-400 border border-success-700/50" : "bg-success-50 text-success-700 border border-success-200"
                  )}>
                    <FileCheck className="w-3.5 h-3.5" />
                    Proposal Received
                  </div>
                );
              }
              if (['awaiting_proposal', 'pending', 'draft', 'progress'].includes(status)) {
                return (
                  <div className={cn(
                    "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium",
                    isDark ? "bg-warning-900/30 text-warning-400 border border-warning-700/50" : "bg-warning-50 text-warning-700 border border-warning-200"
                  )}>
                    <Clock className="w-3.5 h-3.5" />
                    Awaiting Response
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}

        {/* Client-facing hotel note, beside the hotel */}
        {venue.notes?.trim() && (
          <div className="mb-3">
            <ClientNote notes={venue.notes} isDark={isDark} compact />
          </div>
        )}
        
        {/* Actions */}
        <div className="mt-auto pt-3 flex items-center gap-2">
          {onViewDetails && (
            <Button
              variant={isDark ? "ghost" : "outline"}
              size="sm"
              onClick={onViewDetails}
              leftIcon={<Eye className="w-4 h-4" />}
              className={isDark ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""}
            >
              View Details
            </Button>
          )}
          {venue.website && (
            <Button
              variant={isDark ? "ghost" : "outline"}
              size="sm"
              onClick={() => window.open(venue.website!, '_blank', 'noopener,noreferrer')}
              leftIcon={<Globe className="w-4 h-4" />}
              className={isDark ? "text-gray-300 hover:text-white hover:bg-gray-700" : ""}
            >
              Link to Hotel Website
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Tab Button Component
// ============================================
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count: number;
  variant: 'success' | 'primary' | 'secondary';
}

function TabButton({ active, onClick, icon, label, count, variant }: TabButtonProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const variantColors = {
    success: {
      active: isDark ? 'border-success-500 text-success-400' : 'border-success-600 text-success-700',
      inactive: isDark ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'
    },
    primary: {
      active: isDark ? 'border-primary-500 text-primary-400' : 'border-primary-500 text-primary-600',
      inactive: isDark ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'
    },
    secondary: {
      active: isDark ? 'border-gray-400 text-gray-200' : 'border-gray-600 text-gray-800',
      inactive: isDark ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'
    }
  };
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-4 py-3.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 whitespace-nowrap",
        active ? variantColors[variant].active : variantColors[variant].inactive
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      <span className={cn(
        "px-2 py-0.5 rounded-full text-xs",
        active 
          ? (isDark ? "bg-gray-700 text-white" : "bg-gray-200 text-gray-800")
          : (isDark ? "bg-gray-800 text-gray-400" : "bg-gray-100 text-gray-500")
      )}>
        {count}
      </span>
    </button>
  );
}

// ============================================
// Main Research View Component
// ============================================
export function ResearchView() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  // Theme state
  const [theme, setTheme] = useState<ThemeMode>(getStoredTheme);
  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    setStoredTheme(newTheme);
  };
  const isDark = theme === 'dark';
  
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ResearchData | null>(null);
  const [session, setSession] = useState<StoredSession | null>(null);
  const [linkInfo, setLinkInfo] = useState<{ is_public?: boolean } | null>(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState<TabType>('shortlisted');
  const [destinationFilter, setDestinationFilter] = useState<string>('all');
  const [selectedVenue, setSelectedVenue] = useState<VenueData | null>(null);
  
  // Get current user from auth store (for platform user detection)
  const platformUser = useAuthStore((state) => state.user);
  
  // Load data
  useEffect(() => {
    async function init() {
      if (!token) {
        setError('Invalid link');
        setLoading(false);
        return;
      }
      
      // Check if link is public first (treat null/undefined as public)
      try {
        const info = await proposalService.getPublicLinkInfo(token);
        setLinkInfo(info);
        if (info.is_public !== false) {
          // Public link - no authentication required, load data directly
          try {
            const result = await proposalService.getResearchData(token);
            setSession({ 
              token, 
              sessionId: 'public-link', 
              email: 'Public Access',
              expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            });
            setData(result);
            setLoading(false);
            return;
          } catch (err: any) {
            console.error('Failed to load research data for public link:', err);
            setError('Failed to load research data');
            setLoading(false);
            return;
          }
        }
      } catch (err: any) {
        // If we can't get link info, fall through to normal auth flow
        console.log('[ResearchView] Could not check link info, falling back to auth:', err.message);
      }
      
      // First, if user is logged in on the platform, try to load data directly
      if (platformUser) {
        console.log('[ResearchView] Platform user detected, attempting direct access...');
        try {
          // Try to load data - will succeed if user has org access
          const result = await proposalService.getResearchData(token);
          setSession({ 
            token, 
            sessionId: 'platform-user', 
            email: platformUser.email || 'Platform User',
            expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          });
          setData(result);
          setLoading(false);
          console.log('[ResearchView] Platform user access successful');
          return;
        } catch (err: any) {
          // Platform user doesn't have org access, fall through to OTP session
          console.log('[ResearchView] Platform user access failed:', err.message);
        }
      }
      
      // Fall back to OTP session-based access
      const storedSession = getStoredSession(token);
      if (!storedSession) {
        navigate(`/r/${token}`, { replace: true });
        return;
      }
      
      setSession(storedSession);
      
      try {
        // Session ID is in httpOnly cookie, but we pass it in header for backward compatibility
        // During migration, the server will check cookie first, then header
        const result = await proposalService.getResearchData(token, storedSession.sessionId);
        setData(result);
        setLoading(false);
      } catch (err: any) {
        console.error('Failed to load research data:', err);
        if (err.message?.includes('session') || err.message?.includes('401')) {
          if (token) clearSession(token);
          navigate(`/r/${token}`, { replace: true });
        } else {
          setError('Failed to load research data');
        }
        setLoading(false);
      }
    }
    
    init();
  }, [token, navigate, platformUser]);
  
  function handleLogout() {
    if (token) {
      clearSession(token);
    }
    navigate(`/r/${token}`, { replace: true });
  }
  
  function getTabData(): DestinationGroup[] {
    if (!data) return [];
    switch (activeTab) {
      // Shortlisted = approved venues (on the proposal)
      case 'shortlisted': return data.approved || [];
      // Viable = all researched/RFP venues (from RFPs tab)
      case 'approved': return data.shortlisted || [];
      case 'all': return data.all_venues || [];
      default: return [];
    }
  }
  
  function filterVenues(groups: DestinationGroup[]): DestinationGroup[] {
    if (destinationFilter === 'all') return groups;
    return groups.filter(g => `${g.city}-${g.country}` === destinationFilter);
  }
  
  function getAllDestinations(): { key: string; label: string }[] {
    if (!data) return [];
    const all = [...(data.approved || []), ...(data.shortlisted || []), ...(data.all_venues || [])];
    const unique = new Map<string, { city: string; country: string; display_name?: string | null; destination_type?: string }>();
    all.forEach(g => {
      const key = `${g.city}-${g.country}`;
      if (!unique.has(key)) {
        unique.set(key, { city: g.city, country: g.country, display_name: g.display_name, destination_type: g.destination_type });
      }
    });
    return Array.from(unique.entries()).map(([key, v]) => ({
      key,
      // display_name is set via the "Destination Name Override" UI for any
      // destination type (city or region) and is meant to apply everywhere.
      label: v.display_name
        ? `${v.display_name}, ${v.country}`
        : `${v.city}, ${v.country}`
    }));
  }
  
  function getTabCounts() {
    if (!data) return { approved: 0, shortlisted: 0, all: 0 };
    return {
      // shortlisted tab shows approved venues
      shortlisted: (data.approved || []).reduce((sum, g) => sum + g.venues.length, 0),
      // viable tab shows shortlisted (researched/RFP) venues
      approved: (data.shortlisted || []).reduce((sum, g) => sum + g.venues.length, 0),
      all: (data.all_venues || []).reduce((sum, g) => sum + g.venues.length, 0),
    };
  }
  
  // Sort order for venue status: 0 = approved (green), 1 = shortlisted/denied (yellow), 2 = others (red)
  function getVenueStatusSortOrder(venue: VenueData): number {
    if (venue.approval_status === 'approved') return 0;
    if (venue.approval_status === 'denied' || venue.status === 'shortlisted' || venue.status === 'rfp_sent' || venue.status === 'rfp_received') return 1;
    return 2;
  }

  // Sorted tab data: by destination (country, city), and for All Research—green (approved) first within each destination
  const displayTabData = React.useMemo(() => {
    const raw = filterVenues(getTabData());
    const sortedByDestination = raw.slice().sort((a, b) => {
      const c = a.country.localeCompare(b.country);
      if (c !== 0) return c;
      return (a.display_name || a.city || '').localeCompare(b.display_name || b.city || '');
    });
    if (activeTab === 'all') {
      return sortedByDestination.map(g => ({
        ...g,
        venues: g.venues.slice().sort((a, b) => getVenueStatusSortOrder(a) - getVenueStatusSortOrder(b)),
      }));
    }
    return sortedByDestination;
  }, [activeTab, data, destinationFilter]);

  function getAllVenuesFlat(): VenueData[] {
    if (activeTab === 'all') {
      return displayTabData.flatMap(g => g.venues);
    }
    const groups = filterVenues(data?.all_venues || []);
    return groups.flatMap(g => g.venues);
  }

  // Helper function to determine venue status for status dot
  // Create lookup maps once for performance
  const venueStatusMap = React.useMemo(() => {
    if (!data) return { approved: new Set<string>(), shortlisted: new Set<string>() };
    
    const approvedIds = new Set<string>();
    const shortlistedIds = new Set<string>();
    
    // Collect all approved venue IDs (normalize to strings for consistent comparison)
    (data.approved || []).forEach(group => {
      group.venues.forEach(venue => {
        approvedIds.add(String(venue.id));
      });
    });
    
    // Collect all shortlisted venue IDs (includes approved ones too)
    (data.shortlisted || []).forEach(group => {
      group.venues.forEach(venue => {
        const venueId = String(venue.id);
        shortlistedIds.add(venueId);
        // If it's approved, also mark as approved
        if (venue.approval_status === 'approved') {
          approvedIds.add(venueId);
        }
      });
    });
    
    return { approved: approvedIds, shortlisted: shortlistedIds };
  }, [data]);

  function getVenueStatus(venue: VenueData): 'approved' | 'shortlisted' | 'all' {
    // Normalize venue ID to string for consistent comparison
    const venueId = String(venue.id);
    
    // Check venue's own approval_status field first (for venues that have it)
    if (venue.approval_status === 'approved') {
      return 'approved';
    }
    
    // Check lookup maps for fast ID-based lookup
    if (venueStatusMap.approved.has(venueId)) {
      return 'approved';
    }
    
    if (venueStatusMap.shortlisted.has(venueId) || venue.status === 'shortlisted' || venue.status === 'rfp_sent' || venue.status === 'rfp_received' || venue.approval_status === 'denied') {
      return 'shortlisted';
    }
    
    // Default: all researched venues
    return 'all';
  }

  // Notes display component
  // Client-facing note cell (internal notes are never sent by the API)
  function NotesDisplay({ notes, isDark }: { notes?: string | null; isDark: boolean }) {
    if (!notes || notes.trim().length === 0) {
      return <span style={{ color: isDark ? '#4b5563' : '#9ca3af' }}>-</span>;
    }

    const truncateText = (text: string, maxLength: number = 25) => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };

    return (
      <SimpleTooltip content={notes} side="top">
        <div className="flex items-center gap-1.5 min-w-0 cursor-help">
          <StickyNote className={cn("w-3.5 h-3.5 flex-shrink-0", isDark ? "text-blue-400" : "text-blue-600")} />
          <span className={cn("text-sm truncate min-w-0", isDark ? "text-gray-300" : "text-gray-700")}>
            {truncateText(notes)}
          </span>
        </div>
      </SimpleTooltip>
    );
  }

  // Helper function to get status dot color as inline style
  function getStatusDotStyle(status: 'approved' | 'shortlisted' | 'all', isDark: boolean): React.CSSProperties {
    switch (status) {
      case 'approved':
        // Green - using explicit hex colors
        return { backgroundColor: isDark ? '#10b981' : '#059669' }; // green-500 / green-600
      case 'shortlisted':
        // Yellow - using explicit hex colors
        return { backgroundColor: isDark ? '#facc15' : '#eab308' }; // yellow-400 / yellow-500
      default:
        // Red
        return { backgroundColor: isDark ? '#ef4444' : '#dc2626' }; // red-500 / red-600
    }
  }
  
  // Loading state
  if (loading) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center",
        isDark ? "bg-gray-950" : "bg-gray-50"
      )}>
        <div className="text-center">
          <Loader2 className={cn("w-10 h-10 animate-spin mx-auto mb-4", isDark ? "text-secondary-500" : "text-primary-600")} />
          <p className={isDark ? "text-gray-400" : "text-gray-600"}>Loading research data...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error || !data) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center p-4",
        isDark ? "bg-gray-950" : "bg-gray-50"
      )}>
        <div className={cn(
          "max-w-md w-full p-8 text-center rounded-2xl border",
          isDark ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"
        )}>
          <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-error-600" />
          </div>
          <h1 className={cn("text-xl font-semibold mb-2", isDark ? "text-white" : "text-gray-900")}>
            Unable to Load
          </h1>
          <p className={cn("mb-6", isDark ? "text-gray-400" : "text-gray-600")}>
            {error || 'Something went wrong'}
          </p>
          <Button variant="secondary" onClick={() => navigate(`/r/${token}`)}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }
  
  const counts = getTabCounts();
  const destinations = getAllDestinations();
  
  // Generate dynamic meta tags
  const eventName = data?.event?.name || '';
  const clientName = data?.event?.client_name || '';
  const title = `Research for ${eventName}${clientName ? ` for ${clientName}` : ''} | Moniker Partners`;
  const description = `Venue research for ${eventName}${clientName ? ` for ${clientName}` : ''} | Moniker Partners`;
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {/* Open Graph tags */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
      <div className={cn(
        "min-h-screen flex flex-col transition-colors duration-300",
        isDark 
          ? "bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" 
          : ""
      )}
      style={!isDark ? { backgroundColor: '#efede7' } : undefined}
      >
        {/* Header */}
        <header className={cn(
          "sticky top-0 z-20 backdrop-blur-xl border-b",
          isDark 
            ? "bg-gray-900/90 border-gray-800" 
            : "bg-white/90 border-gray-200"
        )}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            {/* Mobile Layout: Stacked */}
            <div className="flex flex-col gap-2 sm:hidden">
              {/* Top Row: Logo + Event Name + Controls */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Moniker Logo Mark - Fixed minimum width */}
                  <div className={cn(
                    "min-w-10 w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center shadow-lg",
                    isDark ? "bg-gray-800" : "bg-white"
                  )}>
                    <img 
                      src="/images/moniker_logo_mark.svg" 
                      alt="Moniker" 
                      className="w-6 h-6 object-contain"
                      style={isDark ? { filter: 'none' } : { filter: 'brightness(0)' }}
                    />
                  </div>
                  {/* Event Name + Venue Research */}
                  <div className="flex-1 min-w-0 flex flex-col">
                    <h1 className={cn("font-semibold text-base leading-tight truncate", isDark ? "text-white" : "text-gray-950")}>
                      {data.event.name}
                    </h1>
                    <div className={cn("text-xs mt-0.5", isDark ? "text-gray-400" : "text-gray-600")}>
                      Venue Research
                    </div>
                  </div>
                </div>
                {/* Controls - Compact */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <ThemeToggle />
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleLogout}
                    className={cn("p-2", isDark ? "text-gray-400 hover:text-white hover:bg-gray-800" : "")}
                    aria-label="Sign Out"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Row 3: Client Name (aligned with logo) */}
              {data.event.client_name && (
                <div className={cn("text-xs pl-[52px]", isDark ? "text-gray-400" : "text-gray-600")}>
                  {data.event.client_name}
                </div>
              )}
              
              {/* Row 4: Dates • Budget • Group Size (aligned with logo) */}
              {(() => {
                const parts: string[] = [];
                
                // Format dates
                const eventDateFields: EventDateFields = {
                  date_type: data.event.date_type || null,
                  event_month: data.event.event_month || null,
                  start_date: data.event.start_date || null,
                  end_date: data.event.end_date || null,
                  date_display_override: data.event.date_display_override || null,
                };
                const formattedDates = formatEventDates(eventDateFields, true);
                if (formattedDates) parts.push(formattedDates);
                
                // Format budget
                const formatBudgetRange = (min?: number | null, max?: number | null) => {
                  if (!min && !max) return undefined;
                  if (min && max) {
                    return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k`;
                  }
                  if (min) return `$${(min / 1000).toFixed(0)}k+`;
                  if (max) return `Up to $${(max / 1000).toFixed(0)}k`;
                  return undefined;
                };
                const budgetRange = formatBudgetRange(data.event.budget_min, data.event.budget_max);
                if (budgetRange) parts.push(budgetRange);
                
                // Format group size
                if (data.event.expected_attendees) {
                  parts.push(`${data.event.expected_attendees} Pax`);
                }
                
                if (parts.length === 0) return null;
                
                return (
                  <div className={cn("text-xs pl-[52px] flex items-center gap-1.5 flex-wrap", isDark ? "text-gray-400" : "text-gray-600")}>
                    {parts.join(' • ')}
                  </div>
                );
              })()}
              
              {/* Row 5: Email (aligned with logo) */}
              {session?.email && (
                <div className={cn("text-xs pl-[52px]", isDark ? "text-gray-500" : "text-gray-500")}>
                  {session.email}
                </div>
              )}
            </div>
            
            {/* Desktop Layout: Horizontal */}
            <div className="hidden sm:flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {/* Moniker Logo Mark - Fixed minimum width */}
                <div className={cn(
                  "min-w-10 w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center shadow-lg",
                  isDark ? "bg-gray-800" : "bg-white"
                )}>
                  <img 
                    src="/images/moniker_logo_mark.svg" 
                    alt="Moniker" 
                    className="w-6 h-6 object-contain"
                    style={isDark ? { filter: 'none' } : { filter: 'brightness(0)' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className={cn("font-semibold text-lg", isDark ? "text-white" : "text-gray-950")}>
                    {data.event.name}
                  </h1>
                  <div className={cn("text-sm flex items-center gap-2 flex-wrap", isDark ? "text-gray-400" : "text-gray-600")}>
                    {data.event.client_name && (
                      <>
                        <span>{data.event.client_name}</span>
                        <span>•</span>
                      </>
                    )}
                    <span>Venue Research</span>
                    {/* Dates, Budget, Group Size */}
                    {(() => {
                      const parts: string[] = [];
                      
                      // Format dates
                      const eventDateFields: EventDateFields = {
                        date_type: data.event.date_type || null,
                        event_month: data.event.event_month || null,
                        start_date: data.event.start_date || null,
                        end_date: data.event.end_date || null,
                        date_display_override: data.event.date_display_override || null,
                      };
                      const formattedDates = formatEventDates(eventDateFields, true);
                      if (formattedDates) parts.push(formattedDates);
                      
                      // Format budget
                      const formatBudgetRange = (min?: number | null, max?: number | null) => {
                        if (!min && !max) return undefined;
                        if (min && max) {
                          return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k`;
                        }
                        if (min) return `$${(min / 1000).toFixed(0)}k+`;
                        if (max) return `Up to $${(max / 1000).toFixed(0)}k`;
                        return undefined;
                      };
                      const budgetRange = formatBudgetRange(data.event.budget_min, data.event.budget_max);
                      if (budgetRange) parts.push(budgetRange);
                      
                      // Format group size
                      if (data.event.expected_attendees) {
                        parts.push(`${data.event.expected_attendees} Pax`);
                      }
                      
                      if (parts.length === 0) return null;
                      
                      return (
                        <>
                          <span>•</span>
                          <span>{parts.join(' • ')}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <ThemeToggle />
                {session?.email && (
                  <span className={cn("text-sm", isDark ? "text-gray-500" : "text-gray-500")}>
                    {session.email}
                  </span>
                )}
                {/* Only show Sign Out button if it's a protected link (not public) */}
                {/* Hide Sign Out for public links (sessionId === 'public-link') or when linkInfo indicates public */}
                {session?.sessionId !== 'public-link' && linkInfo?.is_public !== true && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleLogout}
                    leftIcon={<LogOut className="w-4 h-4" />}
                    className={isDark ? "text-gray-400 hover:text-white hover:bg-gray-800" : ""}
                  >
                    Sign Out
                  </Button>
                )}
              </div>
            </div>
          </div>
        </header>
        
        {/* Tabs */}
        <div className={cn(
          "border-b",
          isDark ? "bg-gray-900/50 border-gray-800" : "bg-white border-gray-200"
        )}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex gap-1 overflow-x-auto scrollbar-hide">
              <TabButton
                active={activeTab === 'shortlisted'}
                onClick={() => setActiveTab('shortlisted')}
                icon={<List className="w-4 h-4" />}
                label="Shortlisted"
                count={counts.shortlisted}
                variant="success"
              />
              <TabButton
                active={activeTab === 'approved'}
                onClick={() => setActiveTab('approved')}
                icon={<Check className="w-4 h-4" />}
                label="Viable"
                count={counts.approved}
                variant="primary"
              />
              <TabButton
                active={activeTab === 'all'}
                onClick={() => setActiveTab('all')}
                icon={<Grid3X3 className="w-4 h-4" />}
                label="All Research"
                count={counts.all}
                variant="secondary"
              />
            </div>
          </div>
        </div>
        
        {/* Filter & Requirements Bar */}
        <div className={cn(
          "border-b",
          isDark ? "bg-gray-900/30 border-gray-800/50" : "bg-white border-gray-100"
        )}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
              {/* Destination Filter */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Filter className={cn("w-4 h-4", isDark ? "text-gray-500" : "text-gray-400")} />
                <select
                  value={destinationFilter}
                  onChange={(e) => setDestinationFilter(e.target.value)}
                  className={cn(
                    "h-9 px-3 rounded-lg text-sm border focus:outline-none focus:ring-2",
                    isDark 
                      ? "bg-gray-800 border-gray-700 text-gray-200 focus:ring-secondary-500/50 focus:border-secondary-500" 
                      : "bg-white border-gray-300 text-gray-700 focus:ring-primary-500/50 focus:border-primary-500"
                  )}
                >
                  <option value="all">All Destinations</option>
                  {destinations.map(d => (
                    <option key={d.key} value={d.key}>{d.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Separator & Requirements */}
              {data?.event?.venue_filters && (
                <>
                  <div className={cn(
                    "hidden md:block w-px h-6 self-center",
                    isDark ? "bg-gray-700" : "bg-gray-200"
                  )} />
                  <ResearchRequirements 
                    venueFilters={data.event.venue_filters} 
                    isDark={isDark}
                  />
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6">
          {/* Visible venue count - reflects the active tab + destination filter */}
          {(() => {
            const visibleCount = displayTabData.reduce((sum, g) => sum + g.venues.length, 0);
            if (visibleCount === 0) return null;
            const tabLabel = activeTab === 'shortlisted' ? 'shortlisted' : activeTab === 'approved' ? 'viable' : 'researched';
            const scopeLabel = destinationFilter === 'all'
              ? 'across all destinations'
              : (() => {
                  const match = destinations.find(d => d.key === destinationFilter);
                  return match ? `in ${match.label}` : '';
                })();
            return (
              <div className={cn(
                "mb-4 flex items-baseline gap-2 text-sm",
                isDark ? "text-gray-400" : "text-gray-600"
              )}>
                <span className={cn("font-semibold", isDark ? "text-white" : "text-gray-900")}>
                  {visibleCount}
                </span>
                <span>
                  {visibleCount === 1 ? 'venue' : 'venues'} {tabLabel} {scopeLabel}
                </span>
              </div>
            );
          })()}
          {displayTabData.length === 0 || displayTabData.every(g => g.venues.length === 0) ? (
            <div className={cn(
              "p-12 text-center rounded-2xl border",
              isDark ? "bg-gray-900/50 border-gray-800" : "bg-white border-gray-200"
            )}>
              <Building2 className={cn("w-14 h-14 mx-auto mb-4", isDark ? "text-gray-700" : "text-gray-300")} />
              <h3 className={cn("font-medium text-lg mb-2", isDark ? "text-white" : "text-gray-950")}>
                No Venues Found
              </h3>
              <p className={isDark ? "text-gray-400" : "text-gray-600"}>
                {destinationFilter !== 'all' 
                  ? 'Try selecting a different destination'
                  : `No ${activeTab === 'approved' ? 'viable' : activeTab === 'shortlisted' ? 'shortlisted' : 'research'} venues yet`}
              </p>
            </div>
          ) : activeTab === 'all' ? (
            // Table View for All Researched
            <>
              {/* Mobile Card View */}
              <div className={cn(
                "md:hidden divide-y",
                isDark ? "divide-gray-800" : "divide-gray-200"
              )}>
                {getAllVenuesFlat().map((venue) => (
                  <div 
                    key={venue.id} 
                    className={cn(
                      "p-4",
                      isDark ? "divide-gray-800" : ""
                    )}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        {/* Status Dot */}
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={getStatusDotStyle(getVenueStatus(venue), isDark)}
                          title={
                            getVenueStatus(venue) === 'approved' ? 'Shortlisted' :
                            getVenueStatus(venue) === 'shortlisted' ? 'Viable' :
                            'All Research'
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className={cn("font-medium", isDark ? "text-white" : "text-gray-950")}>
                              {venue.property_name}
                            </h4>
                            {venue.is_all_inclusive && <AllInclusiveBadge isDark={isDark} compact />}
                          </div>
                          <p className={cn("text-sm", isDark ? "text-gray-400" : "text-gray-500")}>
                            {venue.city}{venue.country ? `, ${venue.country}` : ''}
                          </p>
                        </div>
                      </div>
                      {venue.star_rating && (
                        <span className="text-sm">{'⭐'.repeat(venue.star_rating)}</span>
                      )}
                    </div>
                    <div className={cn("flex flex-wrap gap-3 text-sm", isDark ? "text-gray-400" : "text-gray-500")}>
                      {venue.number_of_rooms && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {venue.number_of_rooms} rooms
                        </span>
                      )}
                      {venue.maximum_capacity && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {venue.maximum_capacity} cap
                        </span>
                      )}
                    </div>
                    {/* Notes on Mobile */}
                    {venue.notes && (
                      <div className="mt-3">
                        <NotesDisplay 
                          notes={venue.notes} 
                          isDark={isDark} 
                        />
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedVenue(venue)}
                      leftIcon={<Eye className="w-3 h-3" />}
                      className={cn("mt-2 px-0", isDark ? "text-gray-400 hover:text-white" : "")}
                    >
                      View Details
                    </Button>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table - Custom implementation for full control */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={isDark ? "border-b border-gray-700" : "border-b border-gray-200"}>
                      <th className={cn(
                        "text-left py-3 px-4 text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>Destination</th>
                      <th className={cn(
                        "text-left py-3 px-4 text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>Property</th>
                      <th className={cn(
                        "text-left py-3 px-4 text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>Rating</th>
                      <th className={cn(
                        "text-right py-3 px-4 text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>Rooms</th>
                      <th className={cn(
                        "text-right py-3 px-4 text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>Capacity</th>
                      <th className={cn(
                        "text-left py-3 px-4 text-xs font-medium uppercase tracking-wider",
                        isDark ? "text-gray-400" : "text-gray-500"
                      )}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayTabData.flatMap((group) =>
                      group.venues.map((venue) => (
                        <tr 
                          key={venue.id}
                          onClick={() => setSelectedVenue(venue)}
                          className={cn(
                            "transition-colors cursor-pointer",
                            isDark 
                              ? "border-b border-gray-800 hover:bg-gray-800/70" 
                              : "border-b border-gray-100 hover:bg-gray-50"
                          )}
                        >
                          <td className={cn("py-3 px-4 text-sm", isDark ? "text-gray-400" : "text-gray-600")}>
                            {group.display_name
                              ? `${group.display_name}, ${group.country}`
                              : `${group.city}, ${group.country}`}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                            {/* Eye Icon Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVenue(venue);
                              }}
                              className={cn(
                                "p-1 rounded transition-colors",
                                isDark ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"
                              )}
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {/* Status Dot */}
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={getStatusDotStyle(getVenueStatus(venue), isDark)}
                              title={
                                getVenueStatus(venue) === 'approved' ? 'Shortlisted' :
                                getVenueStatus(venue) === 'shortlisted' ? 'Viable' :
                                'All Researched'
                              }
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <div style={{ color: isDark ? '#ffffff' : '#030712', fontWeight: 500 }}>
                                  {venue.property_name}
                                </div>
                                {venue.is_all_inclusive && <AllInclusiveBadge isDark={isDark} compact />}
                              </div>
                              <div style={{ color: isDark ? '#9ca3af' : '#6b7280', fontSize: '0.875rem' }}>
                                {venue.city}{venue.country ? `, ${venue.country}` : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {venue.star_rating ? (
                            <StarRating rating={venue.star_rating} size="sm" showEmptyStars={true} />
                          ) : (
                            <span style={{ color: isDark ? '#4b5563' : '#9ca3af' }}>-</span>
                          )}
                        </td>
                        <td className={cn("py-3 px-4 text-right", isDark ? "text-gray-300" : "text-gray-700")}>
                          {venue.number_of_rooms?.toLocaleString() || '-'}
                        </td>
                        <td className={cn("py-3 px-4 text-right", isDark ? "text-gray-300" : "text-gray-700")}>
                          {venue.maximum_capacity?.toLocaleString() || '-'}
                        </td>
                        <td className="py-3 px-4 max-w-[200px]">
                          <NotesDisplay 
                            notes={venue.notes} 
                            isDark={isDark} 
                          />
                        </td>
                      </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            // Card View for Viable/Shortlisted
            <div className="space-y-14">
              {displayTabData.map((group) => (
                <div key={`${group.city}-${group.country}`}>
                  {/* Destination Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center",
                      isDark ? "bg-gray-800" : "bg-gray-100"
                    )}>
                      {group.destination_type === 'region'
                        ? <Layers className="w-5 h-5 text-primary-500" />
                        : <MapPin className="w-5 h-5 text-primary-500" />
                      }
                    </div>
                    <div>
                      <h2 className={cn("font-semibold", isDark ? "text-white" : "text-gray-950")}>
                        {group.display_name || group.city}
                      </h2>
                      <p className={cn("text-sm", isDark ? "text-gray-400" : "text-gray-600")}>
                        {group.country}
                      </p>
                    </div>
                    <Badge
                      variant="secondary"
                      className={cn("ml-auto", isDark && "bg-gray-800 text-gray-300 border-gray-700")}
                    >
                      {group.venues.length} venue{group.venues.length !== 1 ? 's' : ''}
                    </Badge>
                  </div>
                  
                  {/* Venue Cards */}
                  <div className="space-y-4">
                    {group.venues.map((venue) => (
                      <VenueCardDisplay 
                        key={venue.id} 
                        venue={venue} 
                        showApprovalBorder={activeTab === 'shortlisted' || activeTab === 'approved'}
                        onViewDetails={() => setSelectedVenue(venue)}
                        shareLinkToken={token || undefined}
                        sessionId={session?.sessionId === 'platform-user' ? undefined : session?.sessionId}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
        
        {/* Footer */}
        <footer className={cn(
          "py-6 border-t",
          isDark ? "bg-gray-900/50 border-gray-800" : "bg-white border-gray-200"
        )}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center text-sm">
            <p className={isDark ? "text-gray-500" : "text-gray-500"}>
              Research compiled by <span className="text-primary-500">Moniker</span> • Contact your event planner for questions
            </p>
          </div>
        </footer>
        
        {/* Venue Details Modal */}
        <VenueDetailsModal
          isOpen={!!selectedVenue}
          onClose={() => setSelectedVenue(null)}
          venue={selectedVenue}
          shareLinkToken={token || undefined}
          sessionId={session?.sessionId === 'platform-user' ? undefined : session?.sessionId}
        />
        
        {/* Utility Styles */}
        <style>{`
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    </ThemeContext.Provider>
  );
}
